// playback-hifi.ts — EINE Stelle, die jede echte Wiedergabe (Musik, Vorlesen/
// TTS, Bot-Stimme) waehrend des Abspielens auf hochwertige A2DP-Wiedergabe
// schaltet — weg vom blechernen HFP-Telefonprofil ueber Bluetooth. Bei
// Pause/Ende zurueck. No-op im Browser/PWA (native-recorder liefert dort null).
//
// 2026-07-15 (Martin): "Ich moechte Musik UND Transkriptionen UND
// Text-to-Speech immer schoen/hochwertig hoeren, nicht im Telefonmodus."
//
// Design:
//  - Referenz-Zaehlung ueber die aktuell SPIELENDEN Elemente: Hi-Fi bleibt an,
//    solange IRGENDEIN Audio laeuft (Musik + Vorlesen koennen sich ueberlappen),
//    und endet erst, wenn alles still ist.
//  - Entprellt: Vorlesen/TTS spielen abschnitts-/chunkweise (mehrere Elemente
//    hintereinander). Ohne Gnadenfrist wuerde die native Session bei JEDEM
//    Uebergang neu ausgehandelt (setActive(false)->setActive(true)) und hoerbar
//    stocken. Startet in der Frist das naechste Stueck, bleibt sie ununterbrochen.
//  - Idempotent: solange schon hi-fi, wird die Session NICHT neu ausgehandelt.

import { seitenTonMelder } from "./seiten-ton.js";

// Zustandsform von AppDelegate.audioStateDict() (siehe native-recorder.ts).
interface AudioSessionStateLike {
  category?: string;
  mode?: string;
  options?: string[];
  outputs?: string[];
  playbackHiFiActive?: boolean;
  callActive?: boolean;
}

const active = new Set<HTMLMediaElement>();
let hiFiOn = false;
/*
 * ⚠️⚠️ 22.09.2026 (Ticket 2253) — DER SALDO, DEN DIE NATIVE SEITE FUEHRT.
 *
 * `PawbotRecorderEngine.resume()` steigt aus, solange `Tonbelegung.aktiv`
 * ist. Diese Belegung folgt NICHT unserer Menge `active`, sondern den
 * Ereignissen `voice-v8:playback-start` / `-end`, die `applyHiFi` sendet.
 * Zwei getrennte Buchfuehrungen fuer dieselbe Sache — das haeufigste Muster
 * in diesem Haus (siehe „zwei Besitzer").
 *
 * In der Nacht auf den 22.09. kehrte `resume()` ELF Mal still zurueck, bei
 * korrekter Kategorie und LEERER eigener Buchfuehrung. Wenn die Engine
 * trotzdem „belegt" sagt, kann das nur an IHREM Zaehler liegen — und den
 * sieht bisher niemand. Ein verlorenes `-end` (WebView-Neuladen, Bruecke
 * kurz weg) genuegt, und er steht fuer immer auf belegt.
 *
 * Also zaehlen wir mit, was wir gesendet haben. Steht hier beim
 * Fortsetz-Versuch etwas anderes als 0, ist die Frage beantwortet.
 */
let tonSaldo = 0;
let exitTimer: ReturnType<typeof setTimeout> | null = null;
export const EXIT_GRACE_MS = 700;

// 2026-07-15 (Martin, "blecherner Anfang"): Vorwaermen. Der Bluetooth-Umschlag
// HFP->A2DP dauert ~1 s. Stossen wir ihn erst beim Abspiel-Ereignis an, klingt
// die erste Sekunde noch blechern. prewarmHiFi() schaltet schon beim
// Antippen/Synthese-Start um, damit der Handschlag WAEHREND Sprache erzeugt/
// geladen wird laeuft und der erste Ton bereits hochwertig ist. Der Vorwaerm-
// Halt loest sich nach PREWARM_MAX, falls die Wiedergabe doch nicht startet.
let prewarmActive = false;
let prewarmTimer: ReturnType<typeof setTimeout> | null = null;
const PREWARM_MAX_MS = 8000;

function clearPrewarm(): void {
  prewarmActive = false;
  if (prewarmTimer) {
    clearTimeout(prewarmTimer);
    prewarmTimer = null;
  }
}

function anyActive(): boolean {
  return active.size > 0 || prewarmActive;
}

// Ticket 782/777 (2026-07-27, Martin: "Mikrofon-Laufzeichen laeuft permanent"
// + 29 % Akku an einem Vormittag): Der RUHEZUSTAND ist derselbe wie Hi-Fi —
// .playback, Mikro aus. Bisher kannte diese Maschine nur einen Grund dafuer:
// "es spielt etwas". Sonst galt immer .playAndRecord, und weil AppDelegate die
// Session beim Start pinnt und nie zurueckstellt, lief die Aufnahme-Session vom
// App-Start bis zum Beenden durch — daher der Dauerindikator und der
// Dauerverbraucher. Jetzt gibt es den zweiten Grund: Wird das Mikro gerade
// nicht gebraucht, ruht die Session ebenfalls auf .playback.
//
// Startwert bewusst `true` = wie bisher .playAndRecord. Erst wenn ptt.ts das
// Warm-Mikro freigibt (12 s nach dem letzten Bedarf), faellt sie in Ruhe —
// so schaltet beim App-Start nichts unnoetig hin und her.
let mikroBedarf = true;

/** Meldet, ob das Mikro gerade gebraucht wird (Warm-Mikro, PTT, Aufnahme). */
export function setMikroBedarf(aktiv: boolean): void {
  if (mikroBedarf === aktiv) return;
  mikroBedarf = aktiv;
  refresh();
}

// Ticket 780 (2026-07-27): Die BOT-STIMME laeuft bewusst auf .playAndRecord
// — Martins Entscheidung vom 19.07., "sofort sprechen schlaegt schoenen
// Klang", weil Bluetooth Classic nur EINES kann: Hi-Fi oder Mikrofon. Sie
// meldet sich deshalb NICHT als Wiedergabe an (kein hookHiFiPlayback).
//
// Genau daraus entstuende mit der neuen Mikro-Ruhe ein Fehler: Laeuft mitten
// in einer Antwort das 12-Sekunden-Fenster des Warm-Mikros ab, faellt
// `mikroBedarf` auf false — und die Session wuerde WAEHREND des Sprechens auf
// .playback wechseln. Ueber Bluetooth ist das ein hoerbarer Pegelsprung, also
// genau das, was #780 beschreibt. Solange die Bot-Stimme spricht, bleibt die
// Session daher unangetastet.
let ttsLaeuft = false;
/**
 * Waehrend gesprochen wird, bleibt die Kategorie, wie sie ist.
 *
 * ⚠️ Die einzige Ausnahme ist eine laufende Aufnahme: die braucht das
 *    Mikrofon, und ein verschluckter Satzanfang wiegt weniger als eine
 *    verlorene Stunde. Deshalb prueft `refresh` den Frost NUR, solange
 *    `alltagNimmtAuf` falsch ist.
 */
let frostBisTtsEnde = false;
if (typeof window !== "undefined") {
  window.addEventListener("voice-v8:tts-audio-start", (ev) => {
    // 17.10.2026 (stimme-nativ.ts): spielt die App die Stimme selbst, gehört ihr die Tonsitzung — kein Hi-Fi von der Seite
    // (es stellte die Sitzung auf `playback` und nähme dem Wach-Wort das Mikro).
    if ((ev as CustomEvent<{ nativ?: boolean } | null>).detail?.nativ === true) return;
    ttsLaeuft = true;
    /*
     * ⚠️ 07.09.2026 — EINMAL entscheiden, dann nicht mehr anfassen.
     *
     * Martin: *„Beim Vorlesen werden immer noch Teile vom Beginn eines
     * Satzes abgeschnitten."*
     *
     * Hier stand kein `refresh()`. Der Wechsel der Audio-Kategorie fiel
     * damit nicht auf den Sprech-Beginn, sondern auf den naechsten
     * beliebigen Anlass — das Auslaufen des Warm-Mikro-Fensters, einen
     * Zustandswechsel des Rekorders. Ein Kategoriewechsel MITTEN im Ton
     * verschluckt auf iOS den Anfang des laufenden Stuecks. Bei einer
     * Antwort aus fuenf Stuecken passiert das mehrmals.
     *
     * Jetzt faellt die Entscheidung mit dem ersten Ton und friert dann
     * ein.
     */
    refresh();
    frostBisTtsEnde = true;
  });
  window.addEventListener("voice-v8:tts-audio-end", () => {
    frostBisTtsEnde = false;
    ttsLaeuft = false;
    refresh();
  });
  /*
   * 15.09.2026 (Pruefer-Befund): Ein abgebrochener Zug (Barge-in per PTT)
   * sendet bewusst KEIN Ende, sondern `tts-audio-abbruch`. Hier hoerte nur
   * start/end zu — nach jedem Abbruch blieben `ttsLaeuft` und der Frost
   * stehen, bis zum naechsten vollstaendigen Zug. Ein Start ohne Ende.
   */
  window.addEventListener("voice-v8:tts-audio-abbruch", () => {
    frostBisTtsEnde = false;
    ttsLaeuft = false;
    refresh();
  });
}

/**
 * Laeuft gerade eine Alltag-Aufnahme? (01.09.2026, neu gefasst 07.09.2026)
 *
 * Ohne dieses Bit faellt `sessionRuhtAufPlayback` auf true und die
 * Hintergrund-Aufnahme stirbt: `.playback` gibt auf iOS das Mikrofon frei.
 *
 * ⚠️ 07.09.2026 — es hing an einem EREIGNIS, und das war der Fehler.
 *
 * Martin, nach fuenf verlorenen Minuten: *„Jetzt macht er schon
 * waehrenddessen Pause, waehrend ich aufnehme und gar nichts druecke."*
 *
 * Dieses Modul wurde bis heute NUR von Lazy-Features geladen (audio-player,
 * narration-engine, deep-questions-view) — also erst, wenn zum ersten Mal
 * etwas abgespielt wird. Der Zuhoerer auf `voice-v8:recorder-state` wurde
 * damit ebenfalls erst dann angemeldet. Lief die Aufnahme da schon, hat er
 * ihren Start NIE gehoert: der Merker blieb auf `false`, bis der naechste
 * Zustandswechsel kam. In genau diesem Fenster schaltete die erste
 * Wiedergabe die Session auf `.playback` — und die laufende Aufnahme
 * bekam kein Mikrofon mehr.
 *
 * ⚠️ Deshalb ist es jetzt eine BRINGSCHULD: `native-recorder.ts` meldet
 *    jeden Zustand direkt hierher (`setAlltagNimmtAuf`), beim allerersten
 *    genauso wie bei jedem weiteren. Kein Ereignis, keine Reihenfolge,
 *    kein Fenster. Ein Schreiber, eine Wahrheit.
 */
let alltagNimmtAuf = false;

/** Nur `native-recorder.ts` ruft das — es ist der einzige, der es weiss. */
export function setAlltagNimmtAuf(live: boolean): void {
  if (live === alltagNimmtAuf) return;
  alltagNimmtAuf = live;
  refresh();
}

/**
 * Läuft gerade eine Aufnahme IN DER SEITE (Sprechtaste)? (23.09.2026, Ticket 2524)
 *
 * Ticket 2524: Die Aufnahme lief sichtbar, aber ohne Ton und ohne Text.
 *
 * BELEGT (n=1): Am 23.09. um 12:57:19 ging das Mikro nach Vesperas Frage von
 * selbst auf (via=nach-frage, cold, lastTtsEnd=-28ms). Um 12:57:20, etwa eine
 * Sekunde später, verließ diese Datei den Hi-Fi-Modus und schaltete die native
 * Tonsitzung um — MITTEN in der laufenden Seitenaufnahme. Danach 54 s
 * spitze=0 in der Stromwache, keine Mitschrift, 12,07 s fehlen in der Datei.
 * In 288 Aufnahmen vom 10. bis 23.09. war das der einzige Sitzungswechsel
 * mitten in einer Aufnahme.
 *
 * Wie der Wechsel den Strom tötet, ist Vermutung. Belegt ist der Weg vom
 * 12:57:20: der Rückruf der Gnadenfrist. Deshalb halten `refresh()` (schaltet
 * EIN) und dieser Rückruf (schaltet AUS) still, solange die Seite aufnimmt.
 * ⚠️ Es sind nicht die einzigen Wege zur nativen Umschaltung:
 *    `loeseWiedergabeHalt` in dieser Datei und native-recorder.ts
 *    (`raeumeTonFuerAufnahme`, die Fortsetz-Schleife der Tagesaufnahme) rufen
 *    `setPlaybackHiFi(false)` direkt. Die bleiben bewusst frei — sie schalten
 *    zur Aufnahme HIN, nicht von ihr weg.
 *
 * ⚠️ KEINE Frist. Die Gegenprüfung hat gezeigt, dass eine Sperre mit
 *    Zeitgrenze (400 ms) die Zusage wieder aufhebt — die Aufnahme dauert,
 *    solange sie dauert.
 *
 * ⚠️ Nichts wird nachgeholt. Was während der Aufnahme geschieht (Wiedergabe
 *    endet, Mikro-Bedarf wechselt, Gnadenfrist läuft ab), ändert nur die
 *    Buchführung. Endet die Aufnahme, gilt der DANN gültige Zustand — einmal,
 *    über denselben Weg wie jede andere Änderung (`refresh`, samt Gnadenfrist
 *    und Frost).
 *
 * ⚠️ Anders als beim Frost gibt es keine Ausnahme für die Tagesaufnahme: sie
 *    ist während der Sprechtaste weich pausiert (`pauseDayRecorderForMic`)
 *    und setzt erst nach dem Aufräumen fort — über die Notbremse
 *    `loeseWiedergabeHalt`, die bewusst NICHT gesperrt ist.
 *
 * Nur `ptt.ts` ruft das — es ist der einzige, der es weiß. Dieselbe
 * Bringschuld wie bei `setAlltagNimmtAuf`: kein Ereignis, kein Fenster.
 */
let seitenAufnahme = false;

/**
 * Nimmt gerade etwas auf — Tagesaufnahme (nativ oder im Browser) oder eine
 * Seitenaufnahme? Nur lesend, für das stille Nachladen (25.09.2026): eine
 * laufende Aufnahme ist ein Grund, die Seite NICHT neu zu laden.
 */
export function aufnahmeLaeuft(): boolean {
  return seitenAufnahme || alltagNimmtAuf;
}

/**
 * Die beiden Hälften von `aufnahmeLaeuft` einzeln — nur lesend. (07.10.2026,
 * Morse-Hörspur: die Sprechtaste lässt sie schweigen, die Tagesaufnahme hält
 * sie dagegen weich an — morse-spur-halt.ts.)
 */
export function seitenAufnahmeLaeuft(): boolean {
  return seitenAufnahme;
}
export function tagesaufnahmeLaeuft(): boolean {
  return alltagNimmtAuf;
}

export function setSeitenAufnahme(an: boolean): void {
  if (an === seitenAufnahme) return;
  /*
   * ⚠️⚠️ 25.09.2026 — DIE SPERRE HIELT AUCH DEN RÜCKWEG AUF.
   *
   * Beginnt die Aufnahme, während die Sitzung noch auf `.playback` liegt
   * (gerade vorgelesen, Gnadenfrist läuft), blieb sie dort: die Sperre oben
   * ließ auch den Rückruf der Gnadenfrist nicht mehr durch. `.playback` kann
   * nicht aufnehmen — die Seite bekam Stille. Gemessen 22.–25.09.: Drücke mit
   * zuletzt gemeldeter Sitzung `Playback` 5 von 24 leer, bei `PlayAndRecord`
   * 3 von 74; beim Tester 7-mal `ptt-wache-null` (Pegel 0/0/0, 25 s ohne Text).
   *
   * Darum: beginnt eine Seitenaufnahme bei eingeschaltetem Hi-Fi, geht es
   * SOFORT zur Aufnahme hin — das ist die Richtung, die die Sperre nie
   * verbieten wollte (siehe oben: „sie schalten zur Aufnahme HIN").
   * `warteAufAufnahmeSitzung` lässt die Sprechtaste darauf warten.
   */
  // Nur in der App: im Browser gibt es keine native Sitzung, und das
  // Zurückschalten nähme dort das warm gehaltene Mikro weg (Erlaubnis-Dialog).
  if (an && hiFiOn && inDerApp()) {
    if (exitTimer) {
      clearTimeout(exitTimer);
      exitTimer = null;
    }
    hiFiOn = false;
    aufnahmeSitzung = applyHiFi(false, "seitenaufnahme", true);
  }
  seitenAufnahme = an;
  refresh();
}

let aufnahmeSitzung: Promise<void> | null = null;

/** Läuft die Seite in der nativen App? (wie native-voice.ts, ohne dessen Import) */
function inDerApp(): boolean {
  const c = (globalThis as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor;
  return c?.isNativePlatform?.() === true;
}

/**
 * Wartet, bis die Sitzung nach `setSeitenAufnahme(true)` wieder aufnehmen
 * kann — höchstens `hoechstensMs`. Ohne Umschaltung sofort. (25.09.2026)
 */
export function warteAufAufnahmeSitzung(hoechstensMs = 800): Promise<void> | null {
  const p = aufnahmeSitzung;
  aufnahmeSitzung = null;
  // Ohne Umschaltung: null — der Aufrufer wartet dann gar nicht (kein
  // zusätzlicher Zwischenschritt im Start der Sprechtaste).
  if (!p) return null;
  return Promise.race([p, new Promise<void>((r) => setTimeout(r, hoechstensMs))]);
}

/** Session gehoert auf .playback: waehrend Wiedergabe ODER in Mikro-Ruhe. */
function sessionRuhtAufPlayback(): boolean {
  /*
   * ⚠️ 2026-09-02 — DIESE ZEILE MUSS DIE ERSTE SEIN. Martin, nach einem
   * verlorenen Vormittag: *„Das Aufnahmesystem funktioniert nicht mehr, sobald
   * ich aus der App rausgehe oder der Bildschirm schlafen geht. Es ist sehr
   * aergerlich, weil ich jetzt ueber eine halbe Stunde aufgenommen habe —
   * nichts wurde gespeichert."*
   *
   * Genau dieser Satz stand schon in pre.2441 (01.09.): „Record, App
   * verlassen, 30 Minuten fehlen." Der Schutz wurde damals eingebaut — aber
   * als VORLETZTE Zeile dieser Funktion, hinter `anyActive()` und
   * `ttsLaeuft`. Beide geben `true` zurueck, sobald irgendetwas spielt. Der
   * Aufnahme-Schutz wurde damit nie erreicht, sobald Vespera sprach; und sie
   * spricht staendig.
   *
   * BELEGT im Protokoll: JEDE `[audio-diag]`-Meldung vom 01. und 02.09. zeigt
   * `category: AVAudioSessionCategoryPlayback` mit `playbackHiFiActive: true`
   * — kein einziges Mal `playAndRecord`. Auf iOS kann `.playback` NICHT
   * aufnehmen: die Kategorie gibt das Mikrofon frei. Geht die App danach in
   * den Hintergrund, gibt es keine Aufnahme-Session mehr, die iOS am Leben
   * halten muesste — und alles ist weg.
   *
   * ⚠️ DER PREIS, bewusst bezahlt: waehrend einer laufenden Tages-Aufnahme
   * bleibt die Session auf `.playAndRecord`, also im leiseren Telefonprofil,
   * auch wenn Vespera spricht. Das ist genau der Kompromiss, den der lange
   * Kommentar unten abwaegt („Lautstaerke nicht optimal"). Er wird hier
   * anders entschieden als dort, weil die Guetee eines Tons nichts wiegt
   * gegen eine halbe Stunde, die es nicht mehr gibt. Laeuft KEINE Aufnahme,
   * gilt die alte Abwaegung unveraendert weiter.
   */
  if (alltagNimmtAuf) return false;
  if (anyActive()) return true;
  // 2026-07-28 (Martin: "die Lautstaerke ist noch nicht optimal, das
  // Zurueckkehren zu Hi-Fi dauert zu lange") — KORREKTUR von gestern.
  //
  // Gestern stand hier `return false`: waehrend die Bot-Stimme spricht, sollte
  // die Session unangetastet bleiben. Die Absicht war richtig — kein Wechsel
  // MITTEN im Sprechen —, die Richtung aber falsch. `false` heisst
  // .playAndRecord, also Telefonprofil und leise; die Wiedergabe blieb damit
  // fuer ihre ganze Dauer im schwachen Profil haengen.
  //
  // Richtig ist das Gegenteil: Wenn gesprochen wird und nichts aufgenommen
  // werden muss, gehoert die Session auf .playback — das ist der laute,
  // hochwertige Weg. Der befuerchtete Pegelsprung entfaellt, weil ptt.ts das
  // Warm-Mikro jetzt schon beim Sprech-Start freigibt: Der Wechsel faellt mit
  // dem ersten Ton zusammen, statt mittendrin zu passieren.
  if (ttsLaeuft) return true;
  // (Der Aufnahme-Schutz stand bis 02.09. HIER — unerreichbar hinter den
  // beiden `return true` darueber. Er ist an den Anfang gewandert.)
  return !mikroBedarf;
}

/**
 * Über welchen Weg Vespera zuletzt in Hi-Fi gespielt hat. (23.09.2026,
 * Tickets 2496/2506/2527)
 *
 * ⚠️ WOZU: Die Messzeile eines Mikrofon-Starts nennt nur das MIKROFON
 *    (`mik=iPhone-Mikrofon`). Am 23.09. dauerten ab 15:29 fast alle kalten
 *    Starts 1,6 bis 2,0 s statt 0,3 s — und alle fielen in eine Zeit, in
 *    der die Stimme über `BluetoothA2DPOutput` lief. Das Mikrofon blieb das
 *    iPhone, darum sah die Zeile gesund aus und der Melder legte Tickets an.
 *    Gegen die Starts des Tages geprüft: 15 von 16 langsamen hatten zuletzt
 *    Bluetooth, 25 von 26 schnellen den Lautsprecher.
 *
 * ⚠️ Der Weg der letzten Hi-Fi-Wiedergabe, NICHT der Weg im Moment des
 *    Starts: nach `playback-end` meldet die Sitzung oft „Speaker", obwohl
 *    der Bluetooth-Empfänger verbunden bleibt — um 15:30:52 „Speaker", vier
 *    Sekunden später ein Start mit 1,8 s. Ein Etikett für die Diagnose, keine
 *    Grundlage für eine Schaltentscheidung.
 */
let letzterHiFiWeg: string | null = null;

/** Die Ausgänge der Tonsitzung in ein kurzes Wort für die Messzeile. */
export function ausgabeKuerzel(
  outputs: readonly string[] | undefined | null,
): string | null {
  const o = (outputs ?? []).join(",");
  if (!o) return null;
  if (/BluetoothA2DP/i.test(o)) return "bt-a2dp";
  if (/BluetoothHFP/i.test(o)) return "bt-hfp";
  if (/BluetoothLE/i.test(o)) return "bt-le";
  if (/CarAudio/i.test(o)) return "auto";
  if (/Speaker/i.test(o)) return "lautsprecher";
  if (/Receiver/i.test(o)) return "hoerer";
  if (/Headphones/i.test(o)) return "kopfhoerer";
  if (/AirPlay/i.test(o)) return "airplay";
  const kurz = (outputs?.[0] ?? "").replace(/[^\p{L}\p{N}]+/gu, "").toLowerCase();
  return kurz ? kurz.slice(0, 16) : null;
}

/** Siehe {@link letzterHiFiWeg}. `null`, solange noch nichts in Hi-Fi lief. */
export function letzterWiedergabeWeg(jetzt = Date.now()): string | null {
  if (letzterHiFiWeg) return letzterHiFiWeg;
  /*
   * ⚠️ 23.09.2026 (Ticket 2541): nach einem Neuladen stand hier nichts, die
   *    Messzeile trug `aus=?`, und der erste Start über Bluetooth (1,7 s)
   *    galt als langsames iPhone — ein Fehlalarm. Der Weg überlebt jetzt das
   *    Neuladen, aber nur kurz: nach einer Viertelstunde kann ein anderes
   *    Gerät verbunden sein.
   */
  try {
    const roh = sessionStorage.getItem(WEG_SPEICHER);
    const gemerkt = roh ? (JSON.parse(roh) as { weg?: string; zeit?: number }) : null;
    if (gemerkt?.weg && typeof gemerkt.zeit === "number" && jetzt - gemerkt.zeit < WEG_GILT_MS) {
      return gemerkt.weg;
    }
  } catch {
    /* kein Speicher (Privatmodus, Test) — dann eben unbekannt */
  }
  return null;
}

const WEG_SPEICHER = "voice-v8.letzter-hifi-weg";
const WEG_GILT_MS = 15 * 60_000;

/** Den Weg für die Sitzung merken — siehe letzterWiedergabeWeg. */
function merkeWeg(weg: string, jetzt = Date.now()): void {
  letzterHiFiWeg = weg;
  try {
    sessionStorage.setItem(WEG_SPEICHER, JSON.stringify({ weg, zeit: jetzt }));
  } catch {
    /* egal */
  }
}

/** Diagnose (#96): den resultierenden Session-Zustand an den Server melden.
 *  Nur ins Protokoll (`[audio-diag]`), nicht mehr auf den Schirm — siehe unten. */
function reportAndCheck(
  state: AudioSessionStateLike | null,
  hiFiAn: boolean,
  anlass?: string,
): void {
  if (!state) return;
  // Tickets 2496/2506/2527: der Weg der Hi-Fi-Wiedergabe fürs Etikett.
  if (hiFiAn) {
    const weg = ausgabeKuerzel(state.outputs);
    if (weg) merkeWeg(weg);
  }
  try {
    void fetch("/api/debug/audio-state", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({
        /*
         * ⚠️ 21.09.2026 (Ticket 2421) — DAS VORWAERMEN BRAUCHT EINEN EIGENEN
         *    NAMEN. Die Abnahme lautet: „steht BluetoothA2DPOutput VOR dem
         *    ersten Ton?" Seit pre.3106 stoesst die Warteschlange den
         *    Umschlag schon beim Synthese-Start an — und der schrieb
         *    dieselbe Zeile `playback-start` wie der echte erste Ton. Damit
         *    war im Protokoll nicht zu unterscheiden, welche Zeile das
         *    Vorwaermen war und welche die Wiedergabe. Die Abnahme war also
         *    gar nicht messbar.
         */
        when: anlass ?? (hiFiAn ? "playback-start" : "playback-end"),
        state,
      }),
    }).catch(() => {});
  } catch {
    /* egal */
  }
  /*
   * ⚠️ 26.09.2026 — KEINE EINBLENDUNG MEHR.
   *
   * Hier stand seit #96 eine Meldung auf dem Schirm („Audio: PlayAndRecord ·
   * Speaker · hiFi=an“), sobald die Kategorie nicht der erwarteten entsprach
   * — gedacht zum Vorlesen, als es noch kein Protokoll dafür gab. Seit der
   * Wach-Wort-Lauscher das Mikro hält, bleibt die Sitzung beim Vorlesen
   * gewollt auf `PlayAndRecord`; die Meldung kam darum bei fast jedem
   * Vorlesen, obwohl alles stimmte. Martin: *„Die ergibt für den User
   * sowieso keinen Sinn.“* Derselbe Zustand steht vollständig in der Zeile
   * `[audio-diag]` oben — dort wird er gelesen.
   */
}

/**
 * Welchen Weg die Tonsitzung beim ERSTEN TON eines Vorlese-Vorgangs hat.
 * (23.09.2026, Ticket 2421)
 *
 * ⚠️ WOZU: Die Abnahme lautet „steht BluetoothA2DPOutput beim ersten Ton?".
 *    Keine bisherige Zeile beantwortet das. `vorwaermen`/`playback-start`
 *    entstehen nur, wenn umgeschaltet WIRD, und zwar direkt nach dem
 *    Umschalter — vor dem rund 1 s langen Bluetooth-Handschlag. Ist Hi-Fi
 *    schon an, schreibt beim echten ersten Ton gar niemand eine Zeile.
 *
 * Nur lesen: fragt den nativen Zustand ab (AppDelegate.audioStateDict) und
 * meldet ihn als `[audio-diag] when=erster-ton`. Kein Umschalten, keine
 * Meldung auf dem Schirm, kein Warten — die Wiedergabe startet wie bisher.
 * Im Browser gibt es keinen nativen Zustand, dann keine Zeile.
 *
 * ⚠️ `nachMs` — wann der Zustand gelesen wurde, gezählt ab dem Aufruf
 *    (direkt vor play()). Die Abfrage läuft nativ per
 *    DispatchQueue.main.async und steht damit HINTER einem gleichzeitig
 *    angestoßenen setPlaybackHiFi, das setActive/setCategory synchron auf
 *    dem Hauptfaden fährt. Ohne die Zeitangabe sähe ein Stand NACH der
 *    Umschaltung aus wie der Stand beim ersten Ton, und die Abnahme stünde
 *    zu gut da. Ein kleiner Wert heißt „wirklich beim ersten Ton", ein
 *    großer „erst nach etwas anderem" — dann ist die Zeile so nicht zu
 *    werten. (23.09.2026, Ticket 2421, Nachprüfung)
 */
export function meldeErstenTon(): void {
  meldeTonZustand("erster-ton");
}

/**
 * Den nativen Zustand der Tonsitzung unter einem Anlass melden — nur lesen, wie `meldeErstenTon`.
 *
 * 16.10.2026 (Martin, iPhone: „die Stimme wird ganz leise, fällt aus“): `[tts-weiter]`/`[tts-neu]` sagten, DASS das
 * System das Element anhielt, aber nicht, wohin der Ton danach ging. Ob „leise“ die Hörmuschel war (Route `Receiver`
 * statt `Speaker` nach einem Kategoriewechsel, AppDelegate „Stimme zu leise trotz max. Regler“ vom 14.07.) oder
 * die Sitzung selbst, zeigt `outputs`/`category`/`mode`/`options` genau in diesem Augenblick.
 */
export function meldeTonZustand(when: string): void {
  const t0 = performance.now();
  void import("./native-recorder.js")
    .then((m) => m.getAudioSessionState())
    .then((state) => {
      if (!state) return;
      const nachMs = Math.round(performance.now() - t0);
      return fetch("/api/debug/audio-state", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ when, nachMs, state }),
      });
    })
    .catch(() => {});
}

if (typeof window !== "undefined") {
  // Ueber ein Ereignis statt per Import. Ein Zirkel der MODULE waere es
  // nicht (diese Datei importiert ptt.ts nicht; ptt.ts ruft seit 23.09.
  // `setSeitenAufnahme` direkt) — der Kreis liegt in den Ereignissen:
  // ptt.ts hoert auf voice-v8:playback-start/-end aus DIESER Datei.
  window.addEventListener("voice-v8:mikro-bedarf", (e) => {
    const d = (e as CustomEvent<{ aktiv?: boolean }>).detail;
    setMikroBedarf(d?.aktiv === true);
  });
}

function applyHiFi(on: boolean, anlass?: string, ohneEreignis = false): Promise<void> {
  /*
   * ⚠⚠ 22.09.2026 (Ticket 2253) — DER ANLASS WIRD HIER BESTIMMT, NICHT
   *    NACH DEM AWAIT.
   *
   * `anlassFuer` liest `active.size` und `prewarmActive`. Bis der native
   * Umschalter zurueckkommt, sind beide laengst weitergelaufen — das
   * Vorwaermen ist dann schon abgemeldet. Darum stand in ZWANZIG STUNDEN
   * Protokoll kein einziges `vorwaermen`, obwohl Martins App seit pre.3139
   * traegt: die Abnahme zu Ticket 2421 war nicht bloss offen, sie war
   * unmessbar.
   */
  const wie = anlass ?? anlassFuer(on);
  // Genau hier entsteht die native Belegung — also genau hier wird gezaehlt.
  tonSaldo = on ? tonSaldo + 1 : 0;
  // Warm-Mikro (ptt.ts) sperren/freigeben — sonst zwingt es die Session
  // zurueck in .playAndRecord (HFP) und die Wiedergabe bleibt blechern.
  // 25.09.2026: beim Beginn einer Seitenaufnahme KEIN playback-end — ptt.ts
  // würde darauf das Mikro vorwärmen, mitten in den eigenen Start hinein.
  if (!ohneEreignis) {
    try {
      window.dispatchEvent(
        new CustomEvent(on ? "voice-v8:playback-start" : "voice-v8:playback-end"),
      );
    } catch {
      /* kein window (Tests) */
    }
  }
  // Nativer Umschalter — lazy geladen, damit Web-Build/Tests nichts von
  // Capacitor brauchen (identisches Muster wie bisher in audio-player.ts).
  return import("./native-recorder.js")
    .then((m) => m.setPlaybackHiFi(on))
    .then((state) => {
      /*
       * ⚠️ 07.09.2026 — BEIDE Richtungen melden. Bis heute nur `if (on)`,
       *    und dadurch stand in jedem Protokoll ausschliesslich
       *    `.playback`. Man konnte nicht unterscheiden, ob die Session dort
       *    haengen blieb oder ordentlich zurueckkam — genau die Frage, um
       *    die es bei Martins verlorenen Aufnahmen geht.
       */
      reportAndCheck(state, on, wie);
    })
    .catch(() => {});
}

/**
 * Wie die Zeile im Protokoll heissen soll.
 *
 * ⚠️ `active` ist leer, solange nur vorgewaermt wird — genau daran haengt
 *    die Unterscheidung. Spielt schon etwas, ist es ein echter Start.
 */
function anlassFuer(on: boolean): string {
  if (!on) return "playback-end";
  return active.size === 0 && prewarmActive ? "vorwaermen" : "playback-start";
}

/** Zustand nach jeder Aenderung an `active`/Vorwaermen neu bestimmen. */
function refresh(): void {
  // Ticket 2524 (23.09.2026): kein Kategoriewechsel mitten in einer
  // Seitenaufnahme — 12:57:20 kostete genau das 54 s Ton. Das Ende der
  // Aufnahme ruft refresh() erneut (setSeitenAufnahme(false)).
  if (seitenAufnahme) return;
  // Siehe frostBisTtsEnde: kein Kategoriewechsel mitten im Sprechen.
  if (frostBisTtsEnde && !alltagNimmtAuf) return;
  if (sessionRuhtAufPlayback()) {
    // Es spielt etwas -> Hi-Fi an (falls noch nicht) und einen laufenden
    // Beenden-Timer abbrechen.
    if (exitTimer) {
      clearTimeout(exitTimer);
      exitTimer = null;
    }
    if (!hiFiOn) {
      hiFiOn = true;
      void applyHiFi(true);
    }
  } else if (hiFiOn && !exitTimer) {
    // Nichts spielt mehr -> nach kurzer Gnadenfrist zurueck (Uebergaenge glaetten).
    exitTimer = setTimeout(() => {
      exitTimer = null;
      /*
       * ⚠️ 23.09.2026 (Ticket 2524) — DER AUSGANG AUS HI-FI.
       *
       * refresh() schaltet Hi-Fi nur EIN; verlassen wird es allein hier und
       * in der Notbremse. 12:57:20 war also einer dieser beiden Wege — eine
       * Sekunde, nachdem das Mikro aufgegangen war (lastTtsEnd=-28ms), mitten
       * in der Aufnahme. Läuft eine Seitenaufnahme, bleibt die Sitzung, wie
       * sie ist: `hiFiOn` bleibt stehen, weil es nativ noch genau so steht,
       * und das Ende der Aufnahme entscheidet über refresh() neu.
       */
      if (seitenAufnahme) return;
      hiFiOn = false;
      void applyHiFi(false);
    }, EXIT_GRACE_MS);
  }
}

/**
 * Jeden Wiedergabe-Halt loesen — die Aufnahme geht vor. (07.09.2026)
 *
 * ## Woher das kommt
 *
 * Martin: *„Wenn ich mit dir spreche, geht der Rekorder auf Pause, aber er
 * schaltet sich nicht wieder ein. Ich bin in der Pauseposition und kann auch
 * ueber das Widget nicht mehr auf Weiter klicken. Ich muss komplett neu
 * starten."*
 *
 * BELEGT im Protokoll vom 07.09., 06:49:56:
 *
 *     [audio-diag] playback-start  category=AVAudioSessionCategoryPlayback
 *                                  playbackHiFiActive=true
 *
 * und danach **kein einziges `playback-end`** bis 07:10. Auf iOS gibt die
 * Kategorie `.playback` das Mikrofon FREI — in ihr kann kein Rekorder
 * fortsetzen. Weder der Knopf im Zuhause noch der native im Blatt noch der
 * Wiederholer: alle rufen dieselbe Engine, und die bekommt kein Mikro.
 *
 * Warum der Halt haengen blieb: `hookHiFiPlayback` traegt ein Element bei
 * `play` ein und bei `pause`/`ended` wieder aus. Wird ein Element verworfen,
 * ohne dass eines der beiden Ereignisse feuert — Navigation, ein Fehler,
 * ein `src`-Wechsel —, bleibt es fuer immer in der Menge. Ein Halt ohne
 * Halter.
 *
 * ⚠️ Deshalb diese Funktion. Sie ist die Notbremse fuer den Fall, dass die
 *    Aufnahme zurueckwill und etwas sie nicht laesst: Menge leeren,
 *    Vorwaermen abbrechen, Mikro anmelden. Eine Wiedergabe, die dabei
 *    wirklich noch laeuft, meldet sich beim naechsten `play` von selbst
 *    wieder an — sie verliert hoechstens einen Moment Hi-Fi. Eine Aufnahme,
 *    die nicht zurueckkommt, verliert die Stunde.
 */
/**
 * Was haelt die Wiedergabe gerade fest? Reiner Lesezugriff. (14.09.2026)
 *
 * ⚠️ Fuer die Diagnose, wenn das Fortsetzen der Tagesaufnahme scheitert.
 * Heute gab es fuenf solche Faelle. Drei lagen 14-31 Sekunden nach einem
 * Ton- oder PTT-Ereignis — da lief plausibel noch etwas. Einer (17:49:23)
 * hatte ZEHN MINUTEN keinerlei Ton-Aktivitaet davor und scheiterte
 * trotzdem. Welcher Halt dort noch stand, stand nirgends.
 *
 * Nativ entscheidet `Tonbelegung.aktiv` (Telefon, Vespera-Anruf,
 * Wiedergabe), und `resume()` kehrt dann STUMM zurueck. Von hier aus sieht
 * man die Wiedergabe-Seite — die andere Haelfte muss das Geraet sagen.
 */
/**
 * Elemente der BOT-STIMME — nur fuer die Diagnose. (19.09.2026, Ticket 2253)
 *
 * ⚠️ BEWUSST GETRENNT VON `active`. Wer hier einträgt, fordert KEIN Hi-Fi an:
 *    `applyHiFi` fasst diese Menge nicht an. Am 19.07. wurde der Bot-Stimme
 *    das Hi-Fi genommen, damit PTT am Bluetooth-Geraet sofort reagiert
 *    (gemessen 1,4-5,5 s Unterschied) — das bleibt so. Verloren ging dabei
 *    nur der EINTRAG, und damit die Sichtbarkeit in `wiedergabeHaltStand()`.
 *
 *    Am 19.09. um 15:56:37 kostete das eine Tagesaufnahme: das Fortsetzen
 *    scheiterte, die Diagnose meldete `nichts-offen`, und vier Sekunden
 *    davor stand `vorlesen-stueck` im selben Protokoll.
 */
const stimmen = new Set<HTMLMediaElement>();
/**
 * Ticket 2919 (02.10.2026): Ein PCM-Stück der Bot-Stimme hat kein Element —
 * es meldet sich über diesen Zähler an und ab. Gleiche Regel wie oben: nur
 * Diagnose, fordert KEIN Hi-Fi an.
 */
let pcmStimmen = 0;
export function meldePcmStimme(an: boolean): void {
  pcmStimmen = Math.max(0, pcmStimmen + (an ? 1 : -1));
}

/**
 * Ein Stueck der Bot-Stimme fuer die Halt-Diagnose anmelden.
 *
 * ⚠️ Abbruch zaehlt wie Ende. `pause`, `error`, `abort` und `emptied` muessen
 *    mit — ein Element, das verworfen wird, ohne `ended` zu feuern, bliebe
 *    sonst fuer immer in der Menge und die Diagnose meldete dauerhaft eine
 *    laufende Stimme. Dieselbe Falle wie am 14.09. bei `active`.
 */
const diagnoseElemente = new WeakSet<HTMLMediaElement>();
export function hookStimmeDiagnose(audio: HTMLMediaElement): void {
  // Ticket 2919: das eine wiederverwendete Datei-Spieler-Element wird mehrfach angemeldet — nur einmal Hörer anhängen.
  if (diagnoseElemente.has(audio)) return;
  diagnoseElemente.add(audio);
  audio.addEventListener("play", () => {
    stimmen.add(audio);
  });
  const weg = (): void => {
    stimmen.delete(audio);
  };
  for (const ev of ["pause", "ended", "error", "abort", "emptied"]) {
    audio.addEventListener(ev, weg);
  }
}

/** Nur fuer Tests — die Menge zwischen zwei Faellen leeren. */
export function stimmenZuruecksetzen(): void {
  stimmen.clear();
  pcmStimmen = 0;
}

export function wiedergabeHaltStand(): {
  spielend: number;
  hiFi: boolean;
  vorwaermung: boolean;
  /** Spricht die Bot-Stimme gerade? (19.09.2026) */
  stimme: boolean;
  /** Wie viele `playback-start` ohne `-end` — die native Sicht. (22.09.2026) */
  saldo: number;
} {
  return {
    spielend: active.size,
    hiFi: hiFiOn,
    vorwaermung: prewarmActive,
    stimme: stimmen.size > 0 || pcmStimmen > 0,
    saldo: tonSaldo,
  };
}

export function loeseWiedergabeHalt(grund: string): void {
  const hatte = active.size > 0 || prewarmActive;
  active.clear();
  clearPrewarm();
  if (exitTimer) {
    clearTimeout(exitTimer);
    exitTimer = null;
  }
  hiFiOn = false;
  /*
   * ⚠️⚠️ 22.09.2026 (Ticket 2253) — DIESE ZEILE HAT MICH EINEN TAG GEKOSTET.
   *
   * Geschaltet wird weiter BEDINGUNGSLOS: das ist der Sinn der Notbremse,
   * sie traut der eigenen Buchfuehrung gerade nicht. Aber die MELDUNG hiess
   * bis heute `playback-end` — als waere eine Wiedergabe zu Ende gegangen.
   *
   * Gemessen in der Nacht auf den 22.09.: 106 `playback-end` gegen 12
   * `playback-start`. Der Ueberschuss kam aus dieser Zeile, denn jeder
   * Fortsetz-Versuch ruft sie. Ich habe gestern aus dem Protokoll gelesen,
   * jede gescheiterte Fortsetzung liege „sekundengenau auf einem
   * `playback-end`" — und daraus eine Ursache gebaut (pre.3140). Die
   * Richtung war umgekehrt: der Versuch ERZEUGT die Zeile. Ich habe meine
   * eigene Wirkung fuer die Ursache gehalten.
   *
   * Ein eigener Name macht den Unterschied im Protokoll sichtbar.
   */
  void applyHiFi(false, `halt-geloest:${grund}`);
  mikroBedarf = true;
  refresh();
  if (hatte) console.warn(`[hifi] Wiedergabe-Halt geloest (${grund})`);
}

/**
 * Haengt die A2DP-Hi-Fi-Umschaltung an EIN Wiedergabe-Audio-Element:
 * beim Abspielen hochwertig, bei Pause/Ende (entprellt, referenzgezaehlt)
 * zurueck. Mehrfach-Aufruf auf dasselbe Element vermeiden.
 */
export function hookHiFiPlayback(audio: HTMLMediaElement): void {
  // 02.10.2026: das Native erfährt, dass die Seite spielt (seiten-ton.ts) — mit Nachlauf,
  // damit der Abschnittswechsel einer Vorlesung (`emptied`/`abort`, gleich danach `play`) nicht zurücknimmt.
  seitenTonMelder.beobachteElement(audio);
  audio.addEventListener("play", () => {
    active.add(audio);
    clearPrewarm(); // echte Wiedergabe hat den Vorwaerm-Halt uebernommen
    refresh();
    starteAufraeumer();
  });
  const off = (): void => {
    active.delete(audio);
    refresh();
  };
  /*
   * ⚠️⚠️ 14.09.2026 — HIER STANDEN NUR `pause` UND `ended`.
   *
   * Martin: *„Im Widget steht pausiert, aber dann kann ich nicht auf den
   * Wieder-weiter-Knopf klicken."*
   *
   * Die Kette dahinter, im nativen Code nachgelesen:
   *   PawbotRecorderEngine.resume()  ->  `if Tonbelegung.aktiv { return }`
   *   Tonbelegung.aktiv              <-  wiedergabeLaeuft
   *   wiedergabeLaeuft               <-  voice-v8:playback-start/-end
   *   playback-end                   <-  applyHiFi(false)
   *   applyHiFi(false)               <-  diese Menge wird leer
   *
   * Bricht ein Ton AB — Netzfehler, Dekodierfehler, verworfenes Element —
   * feuert WEDER `pause` NOCH `ended`. Das Element bleibt in der Menge, das
   * Ende wird nie gemeldet, und `resume()` kehrt fortan STUMM zurueck: Der
   * Weiter-Knopf ist tot, bis die App neu startet. Nichts davon wirft einen
   * Fehler, nichts steht im Protokoll.
   */
  for (const ev of ["pause", "ended", "error", "abort", "emptied"]) {
    audio.addEventListener(ev, off);
  }
}

/*
 * Der Aufraeumer — weil keine Ereignisliste vollstaendig ist.
 *
 * ⚠️ Die fuenf Ereignisse oben decken ab, woran ich gedacht habe. Genau das
 * ist die Schwaeche: Beim naechsten unbedachten Fall (Element weggeworfen,
 * Quelle getauscht, iOS raeumt im Hintergrund auf) stuende der Weiter-Knopf
 * wieder still. Dieselbe Lehre wie bei der Einkaufs-Fuellwortliste: Es gibt
 * keine Liste aller Faelle.
 *
 * Deshalb prueft dieser Takt den ECHTEN Zustand statt auf Meldungen zu
 * warten: Ein Element, das nicht mehr spielt, gehoert nicht in die Menge.
 * Er laeuft nur, solange ueberhaupt etwas eingetragen ist.
 */
const AUFRAEUM_TAKT_MS = 2000;
let aufraeumTimer: ReturnType<typeof setInterval> | null = null;

function starteAufraeumer(): void {
  if (aufraeumTimer || typeof window === "undefined") return;
  aufraeumTimer = setInterval(() => {
    let entfernt = 0;
    for (const a of [...active]) {
      // `ended` deckt den Normalfall ab, `paused` den Abbruch: Ein
      // Media-Element, dessen Wiedergabe endet, steht danach immer auf
      // paused — auch wenn das Ereignis nie ankam.
      if (a.paused || a.ended) {
        active.delete(a);
        entfernt++;
      }
    }
    if (entfernt > 0) {
      console.warn(
        `[hifi] Aufraeumer: ${entfernt} stille(s) Element(e) entfernt — ` +
          `ein Ende-Ereignis ist ausgeblieben`,
      );
      refresh();
    }
    if (active.size === 0 && aufraeumTimer) {
      clearInterval(aufraeumTimer);
      aufraeumTimer = null;
    }
  }, AUFRAEUM_TAKT_MS);
}

/**
 * Vorwaermen: den A2DP-Umschlag SCHON beim Abspiel-Wunsch (Antippen / Start
 * der Sprach-Synthese) anstossen, damit der Bluetooth-Handschlag parallel zum
 * Erzeugen/Laden laeuft und der erste Ton hochwertig ist. Der Halt loest sich
 * nach PREWARM_MAX von selbst, falls die Wiedergabe doch nicht startet (Mikro
 * nicht dauerhaft blockieren). Beginnt echte Wiedergabe, uebernimmt sie.
 */
export function prewarmHiFi(): void {
  prewarmActive = true;
  if (prewarmTimer) clearTimeout(prewarmTimer);
  prewarmTimer = setTimeout(() => {
    prewarmTimer = null;
    prewarmActive = false;
    refresh();
  }, PREWARM_MAX_MS);
  refresh();
}
